import { useCallback, useState } from 'react';
import { booking as wiredBooking } from '../../api/booking';
import { refusalMessage } from '../../api/errors';
import { dataUrlToBlob } from '../../lib/riderWork';
import ApiPhoto from '../ApiPhoto';
import PackagePhotoCapture from '../PackagePhotoCapture';
import './ParcelSeen.css';

/** Until a rider collects the parcel, its photo can still be added or changed. */
const PHOTO_OPEN = ['awaiting_payment', 'requested', 'offered', 'counter_bid', 'accepted'];

function when(iso) {
  if (!iso) return '';

  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Problem({ deliveryId, problem, reference, bookingApi }) {
  const load = useCallback(() => bookingApi.problemPhoto(deliveryId, problem.id), [bookingApi, deliveryId, problem.id]);
  const what = (problem.reason_labels || []).join(', ');

  return (
    <li className="br-pe-loc__row" style={{ listStyle: 'none', display: 'block' }}>
      <p className="br-pe-loc__addr">{what}</p>
      {problem.note ? <p className="br-pe-service__sub">“{problem.note}”</p> : null}
      <p className="br-pe-service__sub">Reported at {when(problem.reported_at)}</p>
      {problem.has_photo ? <ApiPhoto load={load} alt={`${what}: reported with ${reference}`} className="br-parcel-photo" /> : null}
    </li>
  );
}

/**
 * The parcel as each side saw it, on the client's tracking (Rio, 11 Oct 2026:
 * "who sees and the images we take"):
 *
 * - the client's own photo, which their rider sees once the job is theirs.
 *   Until the rider collects the parcel it can be added (a booking whose
 *   upload failed, or that had none) or changed;
 * - the rider's photo at the pickup;
 * - a problem the rider reported at the pickup, with its photo. The office
 *   sees the same on its delivery page.
 */
export default function ParcelSeen({ delivery, onChanged, bookingApi = wiredBooking }) {
  const id = delivery.id;
  const [sent, setSent] = useState(0);
  const [photo, setPhoto] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const loadOwn = useCallback(() => bookingApi.packagePhoto(id), [bookingApi, id]);
  const loadPickup = useCallback(() => bookingApi.pickupPhoto(id), [bookingApi, id]);

  const open = PHOTO_OPEN.includes(delivery.status);
  const problems = Array.isArray(delivery.problems) ? delivery.problems : [];
  const reference = delivery.reference || '';

  const send = async () => {
    setBusy(true);
    setProblem('');
    try {
      await bookingApi.attachPackagePhoto(id, dataUrlToBlob(photo));
      setPhoto('');
      setSent((n) => n + 1);
      onChanged?.();
    } catch (e) {
      setProblem(refusalMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {problems.length > 0 ? (
        <section className="br-pd-card" aria-label="Reported at the pickup">
          <h2 className="br-pd-heading">Your rider reported a problem</h2>
          <p className="br-pe-service__sub">They saw this at the pickup. The InGo office sees it too. Your rider may call you about it.</p>
          <ul style={{ margin: 0, padding: 0 }}>
            {problems.map((p) => (
              <Problem key={p.id} deliveryId={id} problem={p} reference={reference} bookingApi={bookingApi} />
            ))}
          </ul>
        </section>
      ) : null}

      {delivery.has_pickup_photo ? (
        <section className="br-pd-card" aria-label="The rider's photo at the pickup">
          <h2 className="br-pd-heading">Collected</h2>
          <p className="br-pe-service__sub">Your rider's photo of the parcel when they collected it.</p>
          <ApiPhoto load={loadPickup} alt={`The parcel of ${reference}, as the rider collected it`} className="br-parcel-photo" />
        </section>
      ) : null}

      {delivery.has_package_photo || open ? (
        <section className="br-pd-card" aria-label="Your photo of the parcel">
          <h2 className="br-pd-heading">Your photo of the parcel</h2>
          {delivery.has_package_photo ? (
            <>
              <p className="br-pe-service__sub">Your rider sees it once they take the job, to check the parcel is the right one.</p>
              {/* Keyed by `sent`: a new photo sent loads afresh. */}
              <ApiPhoto key={sent} load={loadOwn} alt={`The parcel of ${reference}, as you booked it`} className="br-parcel-photo" />
            </>
          ) : (
            <p className="br-pe-service__sub">Add one: your rider sees it once they take the job, to check the parcel is the right one.</p>
          )}
          {open ? (
            <>
              <PackagePhotoCapture
                compact
                value={photo}
                onChange={(url) => {
                  setProblem('');
                  setPhoto(url || '');
                }}
                label={delivery.has_package_photo ? 'Change the photo' : 'Add a photo'}
              />
              {problem ? (
                <p className="br-pe-service__sub" role="alert" style={{ color: 'var(--ingo-danger, #b91c1c)' }}>
                  {problem}
                </p>
              ) : null}
              {photo ? (
                <button type="button" className="br-confirm" style={{ marginTop: 10 }} onClick={() => void send()} disabled={busy}>
                  {busy ? 'Sending…' : 'Send the photo'}
                </button>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
